<template>
  <!-- One row of a dropdown menu, same look as the profile menu (layout/UserMenu.vue): 14px dark text, outline icon
       at 70% like CliqSign's menus (danger icons stay full red), roomy rounded row, light brand tint on hover. The
       chosen row has blue bold text and a tick; in a `picker` (a menu that picks a value) it is tinted and semibold
       with no tick, like select lists (MenuListItem). -->
  <div class="app-menu-item d-flex align-center ga-2 px-3 py-2 rounded-lg cursor-pointer"
    :class="{ 'app-menu-item--chosen': active && tick && !picker, 'app-menu-item--open': active && (!tick || picker), 'app-menu-item--danger': danger }" role="menuitem" tabindex="0"
    :aria-current="active || undefined" @click="emit('click', $event)" @keydown.enter.prevent="emit('click', $event)"
    @keydown.space.prevent="emit('click', $event)">
    <slot name="prepend">
      <v-icon v-if="icon" size="20" :color="danger ? 'danger-color' : iconColor" :class="{ 'opacity-70': !danger }">{{ icon }}</v-icon>
    </slot>
    <span class="flex-grow-1 text-body-medium text-wrap"
      :class="danger ? 'text-danger-color' : active && picker ? 'text-brand-color font-weight-semibold'
        : active && tick ? 'text-brand-color font-weight-bold' : 'text-high-emphasis'">
      <slot>{{ title }}</slot>
    </span>
    <slot name="append">
      <v-icon v-if="active && tick && !picker" size="18" color="brand-color">mdi-check</v-icon>
    </slot>
  </div>
</template>

<script setup>
defineProps({
  title: { type: String, default: '' },
  icon: { type: String, default: '' },
  // The value this row stands for is the one chosen
  active: { type: Boolean, default: false },
  // Tick on the chosen row; off where "active" only means "open" (e.g. a filter menu's group list)
  tick: { type: Boolean, default: true },
  danger: { type: Boolean, default: false },
  // A value picker: the chosen row is tinted and semibold with no tick, like select lists
  picker: { type: Boolean, default: false },
  // Row menus (RowActionsMenu) use brand-color icons
  iconColor: { type: String, default: 'grey-darken-1' }
})
const emit = defineEmits(['click'])
</script>

<style scoped>
/* The profile menu's row height and hover tint; Vuetify has no hover background utility. */
.app-menu-item {
  min-height: 40px;
  transition: background-color 0.2s ease;
}

/* Only the row under the mouse is shaded (as in the profile menu), so two rows never look chosen.
   The chosen value shows with blue bold text and a tick instead of a background. */
.app-menu-item:hover,
.app-menu-item:focus-visible {
  background: rgba(var(--brand-color-rgb), 0.08);
  outline: none;
}

/* A filter menu's open group (left column), or a picker's chosen value, stays shaded */
.app-menu-item--open {
  background: rgba(var(--brand-color-rgb), 0.08);
}

.app-menu-item--danger:hover {
  background: rgba(var(--v-theme-error), 0.1);
}
</style>
